const runtimeEnv = import.meta.env || {}

export const DATA_BASE_URL = String(
  runtimeEnv.VITE_LIVE_ODDS_DATA_BASE_URL || 'https://abet-board-data.abetco.workers.dev/data',
).replace(/\/+$/, '')
export const WS_HUB_URL = String(
  runtimeEnv.VITE_LIVE_ODDS_WS_URL || 'wss://abet-live-ws-hub.abetco.workers.dev/ws',
)
export const LIVE_ODDS_ENVIRONMENT = String(runtimeEnv.VITE_LIVE_ODDS_ENVIRONMENT || 'production')
export const STREAM_SCHEMA = 'abet.odds.stream.v2'
const RELEASE_SCHEMA = 'abet.board.release.v1'
const ROW_FIELDS = [
  'book', 'event', 'et_date', 'et_time', 'bet_type', 'period', 'selection',
  'line', 'american', 'state', 'prev', 'dl', 'league', 'pid',
]
function isRecord(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function hasOwn(value, key) {
  return Object.prototype.hasOwnProperty.call(value, key)
}

function assertRecord(value, label) {
  if (!isRecord(value)) throw new TypeError(`${label} must be an object`)
  return value
}

function assertText(value, label) {
  if (typeof value !== 'string' || value.trim() === '') {
    throw new TypeError(`${label} must be a non-empty string`)
  }
  return value
}

function assertUtc(value, label) {
  assertText(value, label)
  if (!Number.isFinite(Date.parse(value))) throw new TypeError(`${label} must be a timestamp`)
  return value
}

function assertSequence(value, label = 'sequence') {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new TypeError(`${label} must be a non-negative integer`)
  }
  return value
}

function keyPart(value) {
  if (value === null) return 'null:'
  if (value === undefined) return 'undefined:'
  if (typeof value === 'number') return `number:${String(value)}`
  if (typeof value === 'boolean') return `boolean:${value ? '1' : '0'}`
  return `string:${encodeURIComponent(String(value))}`
}

export function normalizePeriod(value) {
  if (value === null || value === undefined) return 'Game'
  const normalized = String(value).trim()
  if (normalized === '' || /^(game|full game|match)$/i.test(normalized)) return 'Game'
  return normalized
}

export function exactOfferKey(row) {
  assertRecord(row, 'row')
  return [
    row.league,
    row.event,
    row.et_date,
    normalizePeriod(row.period),
    row.bet_type,
    row.selection,
    row.line,
  ].map(keyPart).join('|')
}

function sourceIdentityParts(row) {
  const parts = []
  const book = row.book_key ?? row.book_id ?? row.book
  if (book !== undefined) parts.push(['book', String(book).trim().toLowerCase()])
  for (const key of ['pid', 'source', 'source_id', 'event_id', 'market_id', 'selection_id', 'offer_id']) {
    if (hasOwn(row, key) && row[key] !== undefined) parts.push([key, row[key]])
  }
  if (isRecord(row.dl)) {
    for (const key of ['book', 'ev', 'mkt', 'sel']) {
      if (hasOwn(row.dl, key) && row.dl[key] !== undefined) parts.push([`dl.${key}`, row.dl[key]])
    }
  }
  return parts
}

export function stableOfferId(row) {
  const identity = sourceIdentityParts(assertRecord(row, 'row'))
    .map(([key, value]) => `${key}=${keyPart(value)}`)
    .join('|')
  return identity ? `${exactOfferKey(row)}|${identity}` : exactOfferKey(row)
}

function canonicalJsonValue(value, seen) {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return value
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) throw new TypeError('canonical JSON rejects non-finite numbers')
    return value
  }
  if (typeof value !== 'object') throw new TypeError('canonical JSON contains an unsupported value')
  if (seen.has(value)) throw new TypeError('canonical JSON rejects cycles')
  seen.add(value)
  const normalized = Array.isArray(value)
    ? value.map((item) => canonicalJsonValue(item, seen))
    : Object.keys(value).sort().reduce((result, key) => {
      result[key] = canonicalJsonValue(value[key], seen)
      return result
    }, {})
  seen.delete(value)
  return normalized
}

export function canonicalJsonBytes(value) {
  const body = JSON.stringify(canonicalJsonValue(value, new Set()))
  return new TextEncoder().encode(body)
}

function digestHex(value) {
  return Array.from(new Uint8Array(value), (byte) => byte.toString(16).padStart(2, '0')).join('')
}

async function sha256Hex(bytes, { digestImpl, cryptoImpl = globalThis.crypto } = {}) {
  const digest = digestImpl
    ? await digestImpl('SHA-256', bytes)
    : await cryptoImpl?.subtle?.digest('SHA-256', bytes)
  if (!digest) throw new Error('SHA-256 digest implementation is unavailable')
  return digestHex(digest)
}

function publisherIdentityValue(row, key) {
  return hasOwn(row, key) && row[key] !== undefined ? row[key] : null
}

export async function publisherRowIdentity(row, options = {}) {
  assertRecord(row, 'publisher row')
  if (/^[a-f0-9]{32}(?::[a-f0-9]{12})?$/i.test(row.stream_id || '')) return row.stream_id.toLowerCase()
  if (/^[a-f0-9]{32}(?::\d+)?$/i.test(row.offer_id || '')) return row.offer_id.toLowerCase()
  const dl = isRecord(row.dl) ? row.dl : {}
  let sourceIdentity = ['ev', 'mkt', 'sel'].map((key) => publisherIdentityValue(dl, key))
  if (!sourceIdentity.some((value) => value !== null && value !== '')) {
    sourceIdentity = ['raw_path', 'source_id', 'pid'].map((key) => publisherIdentityValue(row, key))
  }
  const identity = [
    'book', 'league', 'event', 'et_date', 'bet_type',
  ].map((key) => publisherIdentityValue(row, key))
  identity.push(row.period || 'Game', publisherIdentityValue(row, 'selection'))
  identity.push(publisherIdentityValue(row, 'line'), sourceIdentity)
  return (await sha256Hex(canonicalJsonBytes(identity), options)).slice(0, 32)
}

const PUBLISHER_ROW_KEYS = [
  'book', 'event', 'et_date', 'et_time', 'bet_type', 'period', 'selection', 'sel_raw',
  'line', 'american', 'state', 'prev', 'dl', 'league', 'date_inferred', 'is_primary',
  'pid', 'raw_path', 'source_id', 'stream_id',
]

function compactPublisherRow(row) {
  return PUBLISHER_ROW_KEYS.reduce((result, key) => {
    if (hasOwn(row, key)) result[key] = row[key]
    return result
  }, {})
}

export async function indexRowsByPublisherId(rows, options = {}) {
  if (!Array.isArray(rows)) throw new TypeError('publisher rows must be an array')
  const concurrency = options.identityConcurrency ?? 32
  if (!Number.isSafeInteger(concurrency) || concurrency < 1 || concurrency > 256) {
    throw new TypeError('identityConcurrency must be an integer from 1 to 256')
  }
  const identities = new Array(rows.length)
  let cursor = 0
  const worker = async () => {
    while (cursor < rows.length) {
      const index = cursor++
      identities[index] = await publisherRowIdentity(assertRecord(rows[index], `rows[${index}]`), options)
    }
  }
  await Promise.all(Array.from({ length: Math.min(concurrency, rows.length) }, worker))
  const mapped = {}
  const occurrences = {}
  const compactByBase = {}
  rows.forEach((row, index) => {
    const base = identities[index]
    const occurrence = occurrences[base] ?? 0
    const compact = compactPublisherRow(row)
    const compactCanonical = JSON.stringify(canonicalJsonValue(compact, new Set()))
    if (occurrence && compactByBase[base] !== compactCanonical) {
      throw new Error(`publisher row identity collision: ${base}`)
    }
    mapped[occurrence ? `${base}:${occurrence}` : base] = row
    occurrences[base] = occurrence + 1
    compactByBase[base] = compactCanonical
  })
  return mapped
}

export async function verifyCanonicalJsonArtifact(value, artifact, {
  digestImpl,
  cryptoImpl = globalThis.crypto,
} = {}) {
  validateArtifact('selected shard', artifact)
  const bytes = canonicalJsonBytes(value)
  if (bytes.byteLength !== artifact.canonical_bytes) {
    throw new Error(`canonical byte count mismatch: ${bytes.byteLength} != ${artifact.canonical_bytes}`)
  }
  const actual = await sha256Hex(bytes, { digestImpl, cryptoImpl })
  if (actual !== artifact.sha256.toLowerCase()) throw new Error('canonical SHA-256 mismatch')
  return { canonicalBytes: bytes.byteLength, sha256: actual }
}

function validateArtifact(name, artifact) {
  assertRecord(artifact, `artifact ${name}`)
  if (!/^[a-f0-9]{64}$/i.test(artifact.sha256 || '')) {
    throw new TypeError(`artifact ${name} must have a SHA-256`)
  }
  if (!Number.isSafeInteger(artifact.canonical_bytes) || artifact.canonical_bytes < 0) {
    throw new TypeError(`artifact ${name} must have canonical_bytes`)
  }
}

export function validateReleaseManifest(value) {
  const manifest = assertRecord(value, 'release manifest')
  if (manifest.schema_version !== RELEASE_SCHEMA) {
    throw new TypeError(`unsupported release schema: ${manifest.schema_version || 'missing'}`)
  }
  assertText(manifest.release_id, 'release_id')
  assertUtc(manifest.captured_utc, 'captured_utc')
  if (manifest.generated_utc !== undefined) assertUtc(manifest.generated_utc, 'generated_utc')
  assertRecord(manifest.artifacts, 'artifacts')
  const artifacts = Object.entries(manifest.artifacts)
  if (artifacts.length === 0) throw new TypeError('artifacts must not be empty')
  for (const [name, artifact] of artifacts) validateArtifact(name, artifact)
  return manifest
}

function validateCategory(category, index) {
  assertRecord(category, `categories[${index}]`)
  assertText(category.category, `categories[${index}].category`)
  assertText(category.slug, `categories[${index}].slug`)
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(category.slug)) {
    throw new TypeError(`categories[${index}].slug is unsafe`)
  }
  for (const field of ['n_rows', 'n_bytes']) {
    if (!Number.isSafeInteger(category[field]) || category[field] < 0) {
      throw new TypeError(`categories[${index}].${field} must be a non-negative integer`)
    }
  }
  if (category.by_league !== undefined) assertRecord(category.by_league, `categories[${index}].by_league`)
}

export function validateCategoryManifest(value) {
  const manifest = assertRecord(value, 'category manifest')
  assertUtc(manifest.captured_utc, 'category manifest captured_utc')
  if (!Array.isArray(manifest.categories)) throw new TypeError('categories must be an array')
  const seen = new Set()
  manifest.categories.forEach((category, index) => {
    validateCategory(category, index)
    if (seen.has(category.slug)) throw new TypeError(`duplicate category slug: ${category.slug}`)
    seen.add(category.slug)
  })
  if (manifest.books !== undefined && !Array.isArray(manifest.books)) {
    throw new TypeError('books must be an array')
  }
  return manifest
}

function validateCapturedRow(row) {
  assertRecord(row, 'operation row')
  for (const field of ROW_FIELDS) {
    if (!hasOwn(row, field)) throw new TypeError(`operation row is missing ${field}`)
  }
  for (const field of ['book', 'event', 'et_date', 'et_time', 'bet_type', 'selection', 'league']) {
    assertText(row[field], `operation row ${field}`)
  }
  if (row.period !== null && typeof row.period !== 'string') throw new TypeError('operation row period is invalid')
  if (row.line !== null && typeof row.line !== 'number' && typeof row.line !== 'string') {
    throw new TypeError('operation row line is invalid')
  }
  if (typeof row.american !== 'number' || !Number.isFinite(row.american)) {
    throw new TypeError('operation row american is invalid')
  }
  if (row.dl !== null && !isRecord(row.dl)) throw new TypeError('operation row dl is invalid')
  return row
}

function baseState(state) {
  const current = assertRecord(state, 'state')
  const sequence = current.sequence === undefined ? 0 : assertSequence(current.sequence, 'state.sequence')
  const offers = current.offers === undefined ? {} : assertRecord(current.offers, 'state.offers')
  return { ...current, schemaVersion: STREAM_SCHEMA, sequence, offers }
}

function legacyReason(releaseId) {
  return String(releaseId).startsWith('legacy-') ? `legacy_release_id:${releaseId}` : null
}

function applySnapshotRef(state, envelope) {
  const next = baseState(state)
  const sequence = assertSequence(envelope.sequence)
  if (sequence < next.sequence) throw new RangeError('snapshot_ref is out of order')
  const releaseId = assertText(envelope.release_id, 'snapshot_ref release_id')
  const reason = legacyReason(releaseId)
  assertText(envelope.snapshot_url, 'snapshot_ref snapshot_url')
  if (envelope.captured_utc !== undefined) assertUtc(envelope.captured_utc, 'snapshot_ref captured_utc')
  return {
    ...next,
    sequence,
    releaseId,
    capturedUtc: envelope.captured_utc ?? null,
    snapshotUrl: envelope.snapshot_url,
    expectedRowCount: envelope.row_count ?? null,
    needsSnapshot: true,
    caughtUp: false,
    status: reason ? 'degraded' : 'recovering',
    degradedReason: reason,
  }
}

function applyPublisherChanges(offers, upserts, deletes) {
  if (!Array.isArray(upserts) || !Array.isArray(deletes)) {
    throw new TypeError('delta requires upserts and deletes arrays')
  }
  const next = { ...offers }
  for (const item of upserts) {
    assertRecord(item, 'delta upsert')
    next[assertText(item.id, 'delta upsert id')] = validateCapturedRow(item.row)
  }
  for (const id of deletes) delete next[assertText(id, 'delta delete id')]
  return next
}

function applyDelta(state, envelope) {
  const next = baseState(state)
  const sequence = assertSequence(envelope.sequence)
  if (sequence !== next.sequence + 1) throw new RangeError('delta sequence is out of order or gapped')
  const baseReleaseId = assertText(envelope.base_release_id, 'delta base_release_id')
  if (!next.releaseId || baseReleaseId !== next.releaseId) throw new RangeError('delta base release mismatch')
  const releaseId = assertText(envelope.release_id, 'delta release_id')
  const offers = applyPublisherChanges(next.offers, envelope.upserts, envelope.deletes)
  const reason = legacyReason(releaseId)
  return {
    ...next,
    offers,
    sequence,
    releaseId,
    capturedUtc: envelope.captured_utc ?? next.capturedUtc ?? null,
    snapshotUrl: envelope.snapshot_url ?? next.snapshotUrl ?? null,
    expectedRowCount: envelope.row_count ?? Object.keys(offers).length,
    needsSnapshot: false,
    status: reason ? 'degraded' : 'live',
    degradedReason: reason,
  }
}

function applyHeartbeat(state, envelope) {
  const next = baseState(state)
  if (envelope.sequence !== undefined && assertSequence(envelope.sequence) !== next.sequence) {
    throw new RangeError('heartbeat sequence does not match state')
  }
  const releaseId = assertText(envelope.release_id, 'heartbeat release_id')
  if (next.releaseId && releaseId !== next.releaseId) throw new RangeError('heartbeat release mismatch')
  const heartbeatUtc = envelope.ts ?? envelope.captured_utc ?? null
  if (heartbeatUtc !== null) assertUtc(heartbeatUtc, 'heartbeat timestamp')
  return {
    ...next,
    releaseId,
    capturedUtc: envelope.captured_utc ?? next.capturedUtc ?? null,
    snapshotUrl: envelope.snapshot_url ?? next.snapshotUrl ?? null,
    expectedRowCount: envelope.row_count ?? next.expectedRowCount ?? null,
    heartbeatUtc,
  }
}

function applyCaughtUp(state, envelope) {
  const next = baseState(state)
  if (envelope.sequence !== undefined && assertSequence(envelope.sequence) !== next.sequence) {
    throw new RangeError('caught_up sequence does not match state')
  }
  const releaseId = assertText(envelope.release_id, 'caught_up release_id')
  if (next.releaseId && releaseId !== next.releaseId) throw new RangeError('caught_up release mismatch')
  return {
    ...next,
    releaseId,
    caughtUp: true,
    status: next.degradedReason ? 'degraded' : 'live',
  }
}

export function applyStreamEnvelope(state, envelope) {
  assertRecord(envelope, 'stream envelope')
  if (envelope.schema_version !== STREAM_SCHEMA) throw new TypeError('stream envelope has the wrong schema_version')
  switch (envelope.type) {
    case 'snapshot_ref': return applySnapshotRef(state, envelope)
    case 'delta': return applyDelta(state, envelope)
    case 'heartbeat': return applyHeartbeat(state, envelope)
    case 'caught_up': return applyCaughtUp(state, envelope)
    case 'unavailable': return {
      ...baseState(state),
      status: 'degraded',
      degradedReason: `stream_unavailable:${envelope.reason || 'unknown'}`,
    }
    default: throw new TypeError(`unsupported stream envelope: ${envelope.type || 'missing'}`)
  }
}
